<script>
import { onMounted, onActivated, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useModalStore } from '@/stores/modal.js';
import { useAuthenticationStore } from '@/stores/authentication.js';
import { useToastStore } from '@/stores/toast.js';
import { loadStripe } from '@stripe/stripe-js';

export default {
  setup() {
    // Variables
    const clientSecret = ref('');
    const elements = ref(null);
    const stripe = ref(null);
    const errorMessage = ref('');
    const paymentStatus = ref('');
    const productId = ref('');

    // Stores
    const authenticationStore = useAuthenticationStore();
    const modalStore = useModalStore();
    const toastStore = useToastStore();

    // I18n
    const { tm, t } = useI18n();

    onMounted(async () => {
      stripe.value = await loadStripe(`${import.meta.env.VITE_STRIPE_PUBLIC_KEY}`);
      productId.value = modalStore.currentStripeProduct;

      try {
        const response = await fetch(`${import.meta.env.VITE_BACKEND_URL}/api/v2/servicechek/payments/create-payment-intent`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authenticationStore.currentUser.token}`,
          },
          body: JSON.stringify({
            product_key: productId.value
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          errorMessage.value = errorData.error || t('modal.stripe_payment.failed_to_create_payment_intent');
          return;
        }

        const data = await response.json();

        clientSecret.value = data.clientSecret;

        elements.value = stripe.value.elements({ clientSecret: clientSecret.value });

        const paymentElement = elements.value.create('payment');
        paymentElement.mount('#payment-element');

        } catch (error) {
          errorMessage.value = t('modal.stripe_payment.unable_to_initialize_payment');
        }
    })

    const payNow = async () => {  
      if (!stripe.value || !elements.value || !clientSecret.value) {
        errorMessage.value = t('modal.stripe_payment.payment_form_not_loaded');
        return;
      }

      errorMessage.value = '';

      const { error, paymentIntent } = await stripe.value.confirmPayment({
        elements: elements.value,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: 'if_required'
      });

      if (error) {
        errorMessage.value = error.message;
      } else if (paymentIntent) {
        switch (paymentIntent.status) {
          case 'succeeded':
            paymentStatus.value = 'succeeded';
            toastStore.success(t('toast.quote_package_purchased'));
            break;
          case 'processing':
            paymentStatus.value = 'processing';
            errorMessage.value = t('modal.stripe_payment.payment_processing');
            break;
          case 'requires_payment_method':
            paymentStatus.value = 'failed';
            errorMessage.value = t('modal.stripe_payment.card_declined');
            break;
          case 'requires_action':
            paymentStatus.value = 'failed';
            errorMessage.value = t('modal.stripe_payment.payment_requires_action');
            break;
          default:
            paymentStatus.value = 'failed';
            errorMessage.value = t('modal.stripe_payment.unexpected_payment_status', { status: paymentIntent.status });
            break;
        }
      }
    };

    const backToAutowise = () => {
      modalStore.resetModal();
    }

    return {
      tm,
      t,
      backToAutowise,
      payNow,
      productId,
      paymentStatus,
      stripe,
      errorMessage
    }
  },
};
</script>

<template>
  <div class="justify-center items-center w-full p-4" v-if="paymentStatus != 'succeeded'">
    <div v-if="errorMessage" class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
      {{ errorMessage }}
    </div>
    <div id="payment-element"></div>

    <hr/>

    <div class="flex justify-end mt-4">
      <button @click="backToAutowise()" class="negative-button font-semibold inline-block justify-end hover:opacity-75">{{ tm('modal.stripe_payment.pay_cancel') }}</button>
      <button @click="payNow()" class="positive-button rounded-md font-semibold px-4 py-2 ml-5 hover:opacity-75 mx-4">{{ tm('modal.stripe_payment.pay_confirm') }}</button>
    </div>
  </div>
  <div class="justify-center items-center w-full p-4" v-if="paymentStatus == 'succeeded'">
    <p>{{ tm('modal.stripe_payment.post_payment_gratitude') }}</p>

    <hr/>

    <div class="flex justify-end mt-4">
      <button @click="backToAutowise()" class="positive-button rounded-md font-semibold px-4 py-2 ml-5 hover:opacity-75 mx-4">{{ tm('modal.stripe_payment.close_after_payment_attempt') }}</button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/styles/components/modal";
@import '@/assets/styles/components/modals/delete_account';
</style>
